import { useAuth } from "@clerk/clerk-react";
import {
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Avatar,
  Button,
  Grid,
  Accordion as MuiAccordion,
  Typography,
  styled,
} from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  InboxSidebarOptionKey,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { TargetedCheckInsContext } from "contexts/TargetedCheckIns";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ActionItem, GmailThread } from "schemas/dashboard";
import { EmailAddress, EmailDraft } from "schemas/email";
import { routes } from "schemas/routes";

import Editor from "components/Editor";
import { extensions } from "components/Editor/utils";
import StyledButton from "components/Styled/Button";
import { getPageName } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { generateColorFromString } from "utils/color";
import { sanitize } from "utils/sanitize";
import { trackEvent } from "utils/tracking";

import RecipientAutocomplete from "./RecipientAutocomplete";
import SendButtonGroup from "./SendButtonGroup";
import styles from "./styles";
import { getTomorrow, hasInvalidEmail } from "./utils";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

interface EmailDraftComponentProps {
  actionItem: ActionItem;
  setActionItem: Dispatch<SetStateAction<ActionItem | null>>;
  gmailThread?: GmailThread;
  loading: boolean;
}

export default function EmailDraftComponentAction({
  actionItem,
  setActionItem,
  gmailThread,
  loading,
}: EmailDraftComponentProps) {
  const navigate = useNavigate();
  const { setActionItems, fetchTargetedActionItems } = useContext(
    TargetedCheckInsContext,
  );
  const { setTotals } = useContext(OutreachContactsContext);
  const { emailHealth, setOpenIntegrationDialog } = useContext(
    UserIntegrationsContext,
  );
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { currentUser } = useContext(OrganizationUserContext);
  const [emailDraft, setEmailDraft] = useState<EmailDraft>({
    toContacts: [],
    ccContacts: [],
    body: "",
    subject: "",
  });
  const body = sanitize(emailDraft?.body || "");
  const from = {
    email: userIntegration?.email || currentUser?.email,
    name: currentUser?.name,
  };

  const [sendLoading, setSendLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCcContactsChange = (newContacts: EmailAddress[]) => {
    trackEvent(`Outreach Contact Thread ccContacts Input Changed`, {
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });
    setEmailDraft((prev) => {
      return { ...prev, ccContacts: newContacts };
    });
  };

  const handleToContactsChange = (newContacts: EmailAddress[]) => {
    trackEvent(`Outreach Contact Thread toContacts Input Changed`, {
      "Gmail Thread ID": gmailThread?.id,
      emailDraft,
    });
    setEmailDraft((prev) => {
      return { ...prev, toContacts: newContacts };
    });
  };

  const allParticipants = gmailThread
    ? [
        ...gmailThread.messages[gmailThread.messages.length - 1]?.cc,
        gmailThread.messages[gmailThread.messages.length - 1]?.from_,
        ...gmailThread.messages[gmailThread.messages.length - 1]?.bcc,
        ...gmailThread.messages[gmailThread.messages.length - 1]?.to,
      ]
    : [...emailDraft.toContacts, ...emailDraft.ccContacts];

  const handleSendDraftEmail = async (scheduleAt?: null | string) => {
    if (!emailHealth) {
      setOpenIntegrationDialog(true);
      return;
    }

    if (hasInvalidEmail(emailDraft)) {
      setAlert(
        "Some of the entered email addresses were invalid. Please make sure they are properly formatted.",
        "error",
      );
      return;
    }
    removeActionItemFromListLocally(actionItem.id);
    try {
      setSendLoading(true);
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItem.id}/send`,
        "POST",
        {},
        {
          scheduleAt,
          sendFrom: getPageName(),
        },
      );
      if (scheduleAt) {
        setAlert("Successfully scheduled email!", "success");
      } else {
        setAlert("Successfully sent email!", "success");
      }
    } catch (error) {
      setErrorAlert(error);
      fetchTargetedActionItems(1);
    } finally {
      setSendLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaveLoading(true);
    try {
      const { actionItem } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-drafts/${emailDraft?.id}`,
        "PUT",
        {},
        {
          toContacts: emailDraft?.toContacts,
          ccContacts: emailDraft?.ccContacts,
          body: emailDraft?.body,
          subject: emailDraft?.subject,
        },
      );
      setAlert("Successfully saved draft", "success");
      setActionItem((prev) => ({
        ...prev!,
        emailDrafts: actionItem.emailDrafts,
      }));
      setActionItems((prev) =>
        prev.map((x) => {
          if (x.id !== actionItem.id) {
            return x;
          }
          return {
            ...x,
            description: actionItem.description,
          };
        }),
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (emailDraft?.emailOrder === 1) {
        removeActionItemFromListLocally(actionItem.id);
      } else {
        setActionItem((prev) => {
          if (prev?.emailDrafts) {
            const newEmailDrafts = prev?.emailDrafts.filter(
              (x) => x.id !== emailDraft.id,
            );
            prev.emailDrafts = newEmailDrafts;
            setEmailDraft(newEmailDrafts?.[0]);
          }
          return prev;
        });
      }

      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-drafts/${emailDraft.id}`,
        "DELETE",
      );
      setAlert("Successfully deleted draft", "success");
    } catch (error) {
      setErrorAlert(error);
      fetchTargetedActionItems(1);
    } finally {
      setDeleteLoading(false);
    }
  };

  const removeActionItemFromListLocally = (actionItemId: number) => {
    setActionItem(null);
    setActionItems((prev) => {
      const index = prev.findIndex((x) => x.id === actionItemId);
      if (index !== -1) {
        prev.splice(index, 1);
      }
      setTotals((prev) => ({
        ...prev,
        [InboxSidebarOptionKey.checkins]:
          (prev[InboxSidebarOptionKey.checkins] || 1) - 1,
      }));
      const nextItem = prev?.[index] || prev?.[index - 1];
      if (nextItem?.id) {
        navigate(
          `/${routes.inboxCheckins}/${nextItem.id}/contact/${nextItem?.outreachContactId}/email/${nextItem?.gmailThreadId}`,
        );
      }
      return [...prev];
    });
  };

  const editor = useEditor(
    {
      extensions: extensions(),
      content: body,
      onUpdate({ editor }) {
        setActionItem((prev) => {
          if (!prev || !prev.emailDrafts) return prev;
          const updatedDrafts = prev.emailDrafts.map((draft) => {
            if (draft.id === emailDraft.id) {
              return { ...draft, body: editor.getHTML(), isChanged: true };
            }
            return draft;
          });
          return { ...prev, emailDrafts: updatedDrafts };
        });
        setEmailDraft((prev) => {
          return { ...prev, isChanged: true, body: editor.getHTML() };
        });
      },
    },
    [],
  ) as TipTapEditor;

  useEffect(() => {
    if (
      editor &&
      emailDraft?.body?.length > 0 &&
      editor.getHTML() !== emailDraft?.body
    ) {
      editor.commands.setContent(emailDraft?.body, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor?.getHTML(), emailDraft?.body]);

  useEffect(() => {
    if (!emailDraft?.body && actionItem?.emailDrafts?.[0]) {
      setEmailDraft({ ...actionItem?.emailDrafts?.[0], emailOrder: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionItem?.emailDrafts?.length]);

  return (
    <Accordion
      disableGutters
      square
      expanded={true}
      elevation={0}
      sx={{
        width: "100%",
        borderTop: "none",
        borderBottom: "none",
      }}
    >
      <Grid container sx={styles.container}>
        {actionItem?.emailDrafts?.map((draft, index) => {
          if (index === 0) {
            return (
              <Button
                disableElevation
                sx={[
                  styles.buttonTab,
                  styles.firstBorder,
                  emailDraft?.id === draft?.id ? styles.active : {},
                ]}
                onClick={() =>
                  setEmailDraft({ ...draft, emailOrder: index + 1 })
                }
              >
                Drafted Reply
              </Button>
            );
          } else {
            return (
              <Button
                sx={[
                  styles.buttonTab,
                  index === (actionItem?.emailDrafts?.length || 1) - 1
                    ? styles.lastBorder
                    : {},
                  emailDraft?.id === draft?.id ? styles.active : {},
                ]}
                onClick={() =>
                  setEmailDraft({ ...draft, emailOrder: index + 1 })
                }
              >
                Follow-up {index}
              </Button>
            );
          }
        })}
      </Grid>

      <AccordionSummary sx={styles.summary}>
        <Grid container item xs={12}>
          {from.email && (
            <Grid item xs="auto">
              <Avatar
                sx={{
                  background: generateColorFromString(from.email),
                  textTransform: "uppercase",
                  mr: 1,
                }}
              >
                {from.name?.charAt(0) || from.email.charAt(0)}
              </Avatar>
            </Grid>
          )}
          <Grid item container xs sx={styles.summaryText}>
            <Grid item xs={12}>
              {(from?.name || from?.email) && (
                <Typography>
                  {from.name || from.email}{" "}
                  <Typography
                    component="span"
                    color="textSecondary"
                    variant="caption"
                    sx={{ ml: 1 }}
                  >
                    now
                  </Typography>
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              container
              sx={{ py: 0.5 }}
              spacing={1}
              alignItems="center"
            >
              <Grid item xs="auto">
                <Typography
                  component="div"
                  sx={{ width: 25 }}
                  variant="caption"
                  color="textSecondary"
                >
                  to:
                </Typography>
              </Grid>
              <Grid item xs>
                <RecipientAutocomplete
                  options={allParticipants}
                  value={emailDraft.toContacts}
                  updateValue={handleToContactsChange}
                />
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ py: 0.5 }}
              container
              spacing={1}
              alignItems="center"
            >
              <Grid item xs="auto">
                <Typography
                  component="div"
                  sx={{ width: 25 }}
                  variant="caption"
                  color="textSecondary"
                >
                  cc:
                </Typography>
              </Grid>
              <Grid item xs>
                <RecipientAutocomplete
                  options={allParticipants}
                  value={emailDraft.ccContacts?.filter((x) => x.email)}
                  updateValue={handleCcContactsChange}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Editor editor={editor} allowImage={true} showVariableError />
      </AccordionDetails>

      <Grid container alignItems="center" gap={1} sx={{ p: 2 }}>
        {emailDraft?.emailOrder === 1 && (
          <SendButtonGroup
            handleSendDraftEmail={handleSendDraftEmail}
            sendLoading={sendLoading}
            defaultTime={getTomorrow()}
            disabled={emailDraft.body?.length < 10}
          />
        )}

        <StyledButton
          color="secondary"
          loading={saveLoading}
          onClick={handleSaveDraft}
          disabled={emailDraft.body?.length < 10}
          styles={{ height: 30 }}
        >
          Save Email Draft
        </StyledButton>
        <StyledButton
          loading={deleteLoading}
          color="secondary"
          onClick={handleDelete}
          styles={{ height: 30 }}
        >
          Delete
        </StyledButton>
      </Grid>
    </Accordion>
  );
}
